const ALLOWED_TAGS = new Set([
  'A',
  'B',
  'BLOCKQUOTE',
  'BR',
  'CODE',
  'DD',
  'DIV',
  'DL',
  'DT',
  'EM',
  'FIGCAPTION',
  'FIGURE',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HR',
  'I',
  'IMG',
  'LI',
  'OL',
  'P',
  'PRE',
  'SPAN',
  'STRONG',
  'SUB',
  'SUP',
  'TABLE',
  'TBODY',
  'TD',
  'TFOOT',
  'TH',
  'THEAD',
  'TR',
  'U',
  'UL'
]);

const ALLOWED_ATTRS = {
  A: ['href', 'title', 'class', 'rel', 'target'],
  IMG: ['src', 'alt', 'class', 'width', 'height'],
  TD: ['colspan', 'rowspan'],
  TH: ['colspan', 'rowspan', 'scope'],
  '*': ['class', 'dir', 'id', 'lang']
};

const URL_ATTRS = new Set(['href', 'src']);

function isSafeUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) return false;
  const lower = raw.toLowerCase();
  if (lower.startsWith('javascript:') || lower.startsWith('vbscript:')) {
    return false;
  }
  if (lower.startsWith('data:')) {
    return lower.startsWith('data:image/');
  }
  return true;
}

function sanitizeAttributes(element) {
  const tag = element.tagName;
  const allowed = new Set([
    ...(ALLOWED_ATTRS[tag] || []),
    ...(ALLOWED_ATTRS['*'] || [])
  ]);

  for (const attr of [...element.attributes]) {
    const name = attr.name.toLowerCase();
    if (name.startsWith('on') || !allowed.has(name)) {
      element.removeAttribute(attr.name);
      continue;
    }
    if (URL_ATTRS.has(name) && !isSafeUrl(attr.value)) {
      element.removeAttribute(attr.name);
    }
  }
}

function walk(node) {
  const children = [...node.childNodes];
  for (const child of children) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      if (!ALLOWED_TAGS.has(child.tagName)) {
        const parent = child.parentNode;
        while (child.firstChild) {
          parent.insertBefore(child.firstChild, child);
        }
        parent.removeChild(child);
        continue;
      }
      sanitizeAttributes(child);
      walk(child);
    } else if (child.nodeType === Node.COMMENT_NODE) {
      child.remove();
    }
  }
}

/**
 * Normalize heading levels so injected content does not break page outline.
 * @param {ParentNode} root
 * @param {number} minLevel
 */
export function normalizeHeadings(root, minLevel = 3) {
  const headings = root.querySelectorAll('h1, h2, h3, h4, h5, h6');
  headings.forEach(heading => {
    const level = Number(heading.tagName.slice(1));
    const next = Math.min(6, Math.max(minLevel, level));
    if (next !== level) {
      const replacement = document.createElement(`h${next}`);
      replacement.innerHTML = heading.innerHTML;
      for (const attr of heading.attributes) {
        replacement.setAttribute(attr.name, attr.value);
      }
      heading.replaceWith(replacement);
    }
  });
}

/**
 * Sanitize untrusted HTML using an allowlist.
 * @param {string} html
 * @param {{ minHeadingLevel?: number, normalizeHeadings?: boolean }} [options]
 * @returns {string}
 */
export function sanitizeHtml(html, options = {}) {
  const { minHeadingLevel = 3, normalizeHeadings: shouldNormalize = true } =
    options;
  if (!html) return '';

  const container = document.createElement('div');
  container.innerHTML = String(html);
  walk(container);
  if (shouldNormalize) {
    normalizeHeadings(container, minHeadingLevel);
  }
  return container.innerHTML;
}

export function escapeHtml(text) {
  return String(text ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
